import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";
import { TextQuoteButton } from "../components/home/TextQuoteButton";
import { PriceFactors } from "../components/home/PriceFactors";
import { WarrantySection } from "../components/home/WarrantySection";
import { FinalCta } from "../components/home/OfferReviews";
import { PhotoQuoteSection } from "../components/home/PhotoQuoteForm";

const CITIES = ["Rocky Mount", "Tarboro", "Wilson", "Nashville", "Zebulon", "Raleigh"];
const STEPS = [
  "Text a video of your room so we can see the layout, the walls, and where the TV sits.",
  "We send an itemized quote: the install, plus speakers, receiver, and all equipment quoted separately. You approve it before we book.",
  "One visit. The price we quoted is the price you pay.",
];
const BRANDS = [
  { t: "Receivers", b: "Denon" },
  { t: "Speakers", b: "Triad, Episode, Sonance" },
  { t: "Wireless audio", b: "Sonos" },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "5.1 and 7.1 Surround Sound Installation",
  serviceType: "Home theater surround sound installation",
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: CITIES.map((c) => ({ "@type": "City", name: `${c}, NC` })),
  description: "5.1 and 7.1 surround sound installs, quoted from your video. Speakers, receiver, and all equipment quoted separately by text or call.",
};

export default function SurroundSoundInstall() {
  return (
    <main data-testid="surround-page">
      <SEO
        title="5.1 and 7.1 Surround Sound Installation | Rocky Mount, NC"
        description="5.1 and 7.1 surround sound installation in Rocky Mount, Tarboro, Wilson, Nashville, Zebulon, and Raleigh. Quoted from your video. Speakers, receiver, and equipment quoted separately. Text (252) 545-0011."
        path="/surround-sound-installation"
        jsonLd={schema}
      />
      <section className="bg-[#06142e] text-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 md:py-24">
          <p className="text-[#3b9dff] font-semibold tracking-widest uppercase text-xs">Surround Sound Install</p>
          <h1 className="mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">5.1 and 7.1 Surround Sound Installation</h1>
          <p data-testid="surround-price" className="mt-6 text-2xl font-extrabold text-[#3b9dff]">Quoted from your video</p>
          <p className="mt-3 max-w-2xl text-lg text-slate-300">Speakers, receiver, and all equipment quoted separately by text or call. Serving {CITIES.slice(0, -1).join(", ")}, and Raleigh.</p>
          <TextQuoteButton testId="surround-text-quote-btn" label="Text a video of your room for a quote, (252) 545-0011." className="mt-10" />
        </div>
      </section>
      <section className="section-pad bg-white">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44]">How a surround install works</h2>
          <ol className="mt-8 grid gap-3">
            {STEPS.map((t, i) => (
              <li key={t} data-testid={`surround-step-${i + 1}`} className="flex gap-4 rounded-2xl border border-slate-200 p-5 text-slate-700">
                <span className="text-2xl font-extrabold text-[#3b9dff]/40 leading-none">{i + 1}</span>{t}
              </li>
            ))}
          </ol>
          <h2 className="mt-14 text-2xl md:text-3xl font-extrabold text-[#0a1f44]">Audio brands we carry</h2>
          <ul className="mt-6 grid sm:grid-cols-3 gap-3">
            {BRANDS.map((x) => (
              <li key={x.t} className="rounded-2xl bg-[#f5f8ff] p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{x.t}</p>
                <p className="mt-2 font-bold text-[#0a1f44] flex gap-2"><Check className="w-4 h-4 mt-1 text-[#3b9dff]" />{x.b}</p>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-slate-600">Ask us about a specific model. Buying a TV too?{" "}
            <Link to="/" data-testid="surround-home-offer-link" className="inline-flex items-center gap-1 font-semibold text-[#3b9dff] hover:underline">See the offer <ArrowRight className="w-4 h-4" /></Link>
          </p>
        </div>
      </section>
      <PhotoQuoteSection source="surround" />
      <PriceFactors />
      <WarrantySection />
      <FinalCta />
    </main>
  );
}
